import * as React from 'react';

import * as SliderPrimitive from '@radix-ui/react-slider';

import { Input } from './FormControls';
import { cn } from '../base/utils';

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

const getStepPrecision = (step: number) => {
  const stepText = step.toString().toLowerCase();

  if (stepText.includes('e-')) {
    const [base, exponentPart] = stepText.split('e-');
    const exponent = Number(exponentPart);
    const decimals = (base.split('.')[1] || '').length;
    return exponent + decimals;
  }

  return (stepText.split('.')[1] || '').length;
};

export const normalizeSliderNumber = (value: number, options: { min?: number; max?: number; step?: number }) => {
  const { min, max, step } = options;

  let nextValue = value;

  if (isFiniteNumber(min)) nextValue = Math.max(nextValue, min);
  if (isFiniteNumber(max)) nextValue = Math.min(nextValue, max);

  if (isFiniteNumber(step) && step > 0) {
    const base = isFiniteNumber(min) ? min : 0;
    const precision = getStepPrecision(step);
    nextValue = Number((base + Math.round((nextValue - base) / step) * step).toFixed(precision));
  }

  if (isFiniteNumber(min)) nextValue = Math.max(nextValue, min);
  if (isFiniteNumber(max)) nextValue = Math.min(nextValue, max);

  return nextValue;
};

export const formatSliderNumber = (value?: number) => (isFiniteNumber(value) ? `${value}` : 'NaN');

export interface SliderProps extends Omit<React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root>, 'onChange'> {
  // onChange?: (value: number) => void;
  label?: string;
  disabledLabel?: boolean;
  editableValue?: boolean;
  showRangeLabels?: boolean;
  valueFormatter?: (value: number | undefined) => string;
  rangeLabelFormatter?: (value: number) => string;
  onEditableValueCommit?: (value: number) => void;
}

const Slider = React.forwardRef<React.ElementRef<typeof SliderPrimitive.Root>, SliderProps>(
  (
    {
      className,
      value,
      min,
      max,
      step,
      disabledLabel,
      editableValue,
      showRangeLabels,
      valueFormatter = formatSliderNumber,
      rangeLabelFormatter = formatSliderNumber,
      onEditableValueCommit,
      ...props
    },
    ref,
  ) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const currentValue = value?.[0];
    const hasCurrentValue = isFiniteNumber(currentValue);
    const hasRange = isFiniteNumber(min) && isFiniteNumber(max);
    const useEnhancedLayout = Boolean(editableValue || showRangeLabels);

    const [isEditingValue, setIsEditingValue] = React.useState(false);
    const [draftValue, setDraftValue] = React.useState(hasCurrentValue ? String(currentValue) : '');
    const showEditingLayout = useEnhancedLayout && isEditingValue;

    React.useEffect(() => {
      if (isEditingValue) return;
      setDraftValue(hasCurrentValue ? String(currentValue) : '');
    }, [currentValue, hasCurrentValue, isEditingValue]);

    React.useEffect(() => {
      if (!isEditingValue) return;

      const rafId = window.requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });

      return () => window.cancelAnimationFrame(rafId);
    }, [isEditingValue]);

    const cancelEditingValue = React.useCallback(() => {
      setDraftValue(hasCurrentValue ? String(currentValue) : '');
      setIsEditingValue(false);
    }, [currentValue, hasCurrentValue]);

    const commitEditingValue = React.useCallback(() => {
      if (!editableValue || !onEditableValueCommit) {
        setIsEditingValue(false);
        return;
      }

      const trimmedValue = draftValue.trim();
      if (!trimmedValue) {
        cancelEditingValue();
        return;
      }

      const parsedValue = Number(trimmedValue);
      if (!Number.isFinite(parsedValue)) {
        cancelEditingValue();
        return;
      }

      const normalizedValue = normalizeSliderNumber(parsedValue, { min, max, step });
      onEditableValueCommit(normalizedValue);
      setDraftValue(String(normalizedValue));
      setIsEditingValue(false);
    }, [cancelEditingValue, draftValue, editableValue, max, min, onEditableValueCommit, step]);

    if (!showEditingLayout) {
      return (
        <div className={cn('flex items-center gap-2 [&_span]:flex', className)}>
          <div className="flex min-h-6 flex-1">
            <SliderPrimitive.Root
              ref={ref}
              className={cn('relative flex w-full touch-none select-none items-center')}
              value={value}
              min={min}
              max={max}
              step={step}
              {...props}
            >
              <SliderPrimitive.Track className="relative h-2 w-full grow overflow-hidden rounded-md bg-muted">
                <SliderPrimitive.Range className="absolute h-full bg-muted-foreground" />
              </SliderPrimitive.Track>
              <SliderPrimitive.Thumb className="block h-5 w-2 cursor-grab rounded-sm border-0 bg-muted-foreground ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 " />
            </SliderPrimitive.Root>
          </div>
          {!disabledLabel &&
            (editableValue && onEditableValueCommit ? (
              <button
                type="button"
                className="border-0 bg-transparent p-0 text-sm font-medium text-foreground"
                onClick={() => setIsEditingValue(true)}
              >
                {valueFormatter(currentValue)}
              </button>
            ) : (
              <span className="text-sm font-medium text-foreground">{valueFormatter(currentValue)}</span>
            ))}
        </div>
      );
    }

    return (
      <div className={cn('grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 gap-y-1 [&_span]:flex', className)}>
        <div className="col-start-1 row-start-1 flex min-h-8 min-w-0 items-center">
          <SliderPrimitive.Root
            ref={ref}
            className={cn('relative flex h-5 w-full touch-none select-none items-center')}
            value={value}
            min={min}
            max={max}
            step={step}
            {...props}
          >
            <SliderPrimitive.Track className="relative flex h-2 w-full grow overflow-hidden rounded-md bg-muted">
              <SliderPrimitive.Range className="absolute h-full bg-muted-foreground" />
            </SliderPrimitive.Track>
            <SliderPrimitive.Thumb className="z-10 block h-5 w-2.5 shrink-0 cursor-grab rounded-sm border-0 bg-muted-foreground ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 " />
          </SliderPrimitive.Root>
        </div>

        {!disabledLabel && (
          <div className="col-start-2 row-start-1 flex min-h-8 items-center">
            {editableValue && onEditableValueCommit ? (
              isEditingValue ? (
                <Input
                  ref={inputRef}
                  type="number"
                  inputMode="decimal"
                  value={draftValue}
                  min={min}
                  max={max}
                  step={step}
                  className="h-7 w-[66px] px-2 py-1 text-right text-sm tabular-nums"
                  onChange={(event) => setDraftValue(event.target.value)}
                  onBlur={commitEditingValue}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      event.stopPropagation();
                      commitEditingValue();
                      return;
                    }

                    if (event.key === 'Escape') {
                      event.preventDefault();
                      event.stopPropagation();
                      cancelEditingValue();
                    }
                  }}
                />
              ) : (
                <button
                  type="button"
                  className="inline-flex h-7 w-[66px] items-center justify-end rounded-md border border-transparent bg-transparent px-2 py-1 text-right text-sm font-medium tabular-nums text-foreground transition-colors hover:border-input hover:bg-accent/40"
                  onClick={() => setIsEditingValue(true)}
                >
                  {valueFormatter(currentValue)}
                </button>
              )
            ) : (
              <span className="inline-flex h-7 w-[66px] items-center justify-end px-2 text-right text-sm font-medium tabular-nums text-foreground">
                {valueFormatter(currentValue)}
              </span>
            )}
          </div>
        )}

        {showRangeLabels && hasRange && (
          <div className="col-start-1 row-start-2 flex items-center justify-between px-0.5 text-xs text-muted-foreground">
            <span>{rangeLabelFormatter(min)}</span>
            <span>{rangeLabelFormatter(max)}</span>
          </div>
        )}
      </div>
    );
  },
);
Slider.displayName = SliderPrimitive.Root.displayName;

export { Slider };
